import React, {useCallback, useMemo, useState} from 'react';
import {AgGridReact} from "ag-grid-react";
import {LocalizationAgGrid} from "../../../utils/localization-ag-grid";
import Button from "../../../components/button";
import RemoveButtonCellRenderer from "../../../components/cell-renderer/remove-button-cell-renderer";
import InputForm from "../../../components/input-form";
import {FaPlus} from "react-icons/fa";
import {toast} from "react-toastify";
import {AlertMessage} from "../../../utils/alert-message";
import {MaskDefault} from "../../../utils/mask";
import {useQuery} from "@tanstack/react-query";
import userService from "../../../services/user-service";
import SelectComponent from "../../../components/select-component";
import Modal from "../../../components/modal";
import EquipmentButtons from "./equipment-buttons";
import phoneRender from "../../dashboard/components/phone-render";
import InputCheckbox from "../../../components/input-checkbox";
import TruncateCellRenderer from "../../../components/cell-renderer/truncate-cell-renderer";

const initialState = {
    name: "",
    equip: "",
    phone: "",
    note: "",
    email: "",
    weekly_report: false,
}

const phoneMask = (value) => {
    if (!value) return "";
    return value
        .replace(/\D/g, '')
        .replace(/(\d{2})(\d)/, "($1) $2")
        .replace(/(\d)(\d{4})$/, "$1-$2");
}

const ContactsTable = ({ contacts, gridRef, id, wrapperClass, resume = false, tableClass, readOnlyTable, onClose, submit, onlyClose, autoSubmitOnModalSave = true }) => {
    const [contact, setContact] = useState(initialState);
    const [open, setOpen] = useState(false);
    const [modalTitle, setModalTitle] = useState("")
    const [editingRowIndex, setEditingRowIndex] = useState(null)
    const [newRow, setNewRow] = useState(false)
    const [modalApi, setModalApi] = useState(null)
    const [modalNode, setModalNode] = useState(null)

    const modalRender = useCallback((params, title, api, node) => {
        setModalNode(node);
        setModalApi(api);
        setContact(params?.node.data);
        setEditingRowIndex(params?.node.id);
        setModalTitle(title);
        setOpen(true);
    }, []);

    const columnDefs = useMemo(() => {
        const baseCols = [
            { field: "name", headerName: "Nome" },
            { field: "equip", headerName: "Revenda", cellRenderer: TruncateCellRenderer },
            { field: "phone", headerName: "Número", cellRenderer: phoneRender, width: 155 },
            { field: "email", headerName: "Email", width: 120, cellRenderer: TruncateCellRenderer },
            {
                field: "weekly_report",
                headerName: "Relatório",
                cellClass: "justify-center",
                sortable: false,
                width: 80,
                ...(readOnlyTable && { cellEditor: "agCheckboxCellEditor" })
            },
            { field: "note", headerName: "Observação", flex: 1, cellRenderer: TruncateCellRenderer, minWidth: readOnlyTable ? 250 : 146 }
        ];

        if (!readOnlyTable) {
            baseCols.push({
                field: "",
                sortable: false,
                width: 100,
                editable: false,
                cellRenderer: EquipmentButtons,
                cellRendererParams: {
                    modalRender: modalRender,
                    title: "Contato"
                }
            });
        }
        return baseCols;
    }, [readOnlyTable, modalRender]);

    const { data: projectUsers } = useQuery({
        queryFn: () => userService.getUserByProject(id).then(({ data }) => data),
        queryKey: ['users', { id }],
        enabled: !!id,
        initialData: []
    });

    const defaultColDef = useMemo(() => ({resizable: false}), []);

    const showColDef = useMemo(() => ({editable: false, suppressMenu: true, cellStyle: { cursor: 'default' }}), []);

    const handleInput = useCallback(({ target: { id, value } }) => {
        setContact(prev => ({ ...prev, [id]: value }));
    }, []);

    const handleBlur = useCallback(({ target: { id, value } }) => {
        const cleanId = newRow ? id.replace("contact_", "") : id;
        setContact(prev => ({ ...prev, [cleanId]: value }));
    }, [newRow]);

    const handleSelect = useCallback((id, value) => {
        const textValue = value ? (value.label || value) : "";
        let user_id

        typeof value?.value === "number" ? user_id = value.value : user_id = ""

        setContact(prev => ({
            ...prev,
            [id]: textValue,
            phone: value?.phone || "",
            user_id: user_id,
            email: value?.email || "",
            equip: value?.resale || "",
        }));
    }, []);

    const addContact = useCallback(() => {
        const contactName = typeof contact.name === 'object' ? contact.name?.label : contact.name;

        if (!contactName || !contact.phone) {
            toast.error(AlertMessage.generalEmpty);
            return;
        }

        const newContact = {
            ...contact,
            name: contactName,
            phone: phoneMask(contact.phone)
        };

        gridRef.current?.api.applyTransaction({ add: [newContact] });
        setContact(initialState);
        setNewRow(false);
        if (autoSubmitOnModalSave) submit();
    }, [autoSubmitOnModalSave, contact, gridRef, submit]);

    const onCellClicked = async (cell) => {
        if (!cell.value) return;

        const columnName = cell.colDef.headerName;
        if (columnName !== "Observação" && columnName !== "Número") {
            try {
                await navigator.clipboard.writeText(cell.value);
                const suffix = columnName === "Revenda" ? "a" : "o";
                toast.success(`${columnName} copiad${suffix}.`);
            } catch (err) {
                console.error("Falha ao copiar texto: ", err);
            }
        }
    };

    const findContactName = useCallback((projectUsers, givenName) => {
        if (!givenName) return "";

        const foundUser = projectUsers?.find(opt => opt.label?.includes(givenName));
        if (foundUser) return foundUser;

        return { value: givenName, label: givenName };
    }, []);

    return (
        <div className={wrapperClass}>
            <div className={`ag-theme-quartz grid mt-4 ${tableClass || ''}`}>
                <AgGridReact
                    ref={gridRef}
                    rowHeight={32}
                    rowData={contacts}
                    columnDefs={columnDefs}
                    defaultColDef={!resume ? defaultColDef : showColDef}
                    animateRows={false}
                    suppressRowClickSelection={true}
                    onCellClicked={onCellClicked}
                    gridOptions={{ localeText: LocalizationAgGrid }}
                    domLayout='autoHeight'
                    singleClickEdit={true}
                    stopEditingWhenCellsLoseFocus={true}
                    suppressCellFocus={resume}
                    context={{ readOnlyTable }}
                />
            </div>
            {
                !resume && (
                    <div className="justify-left mt-2">
                        <Button
                            onClick={() => {
                                setModalTitle("Contato");
                                setOpen(true);
                                setNewRow(true);
                                setContact(initialState);
                            }}
                            target="primary"
                        >
                            <FaPlus />
                        </Button>
                    </div>
                )
            }

            <Modal isOpen={open} onClose={() => {onlyClose(setOpen, setNewRow)}} title={modalTitle} overflow={true}>
                <div className="flex flex-col lg:grid grid-cols-1 gap-x-6 gap-y-4 lg:grid-cols-12 m-5">
                    <div className="col-span-3 text-gray-900">
                        <SelectComponent
                            isCreatable
                            instanceId="contact-name"
                            label="Nome *"
                            options={projectUsers}
                            value={findContactName(projectUsers, contact.name, newRow)}
                            onChange={(value) => handleSelect("name", value)}
                            name="name"
                            formatCreateLabel={(inputValue) => `${inputValue}`}
                        />
                    </div>
                    <InputForm id="equip" label="Revenda" col="col-span-2" value={contact.equip} onChange={handleInput} onBlur={handleBlur} />
                    <InputForm id="phone" label="Número *" col="col-span-2" value={contact.phone}
                               onChange={handleInput} mask={MaskDefault.phone} onBlur={handleBlur} />
                    <div className="flex gap-4 col-span-5">
                        <InputForm id="email" label="E-mail" col="w-full" value={contact.email} onChange={handleInput} onBlur={handleBlur} />

                        <InputCheckbox id="weekly_report" name="weekly_report" label="Relatório"
                                       onChange={({ target: { checked } }) => setContact(prev => ({
                                           ...prev,
                                           weekly_report: checked
                                       }))}
                                       checked={contact.weekly_report} />
                    </div>

                    <InputForm id="note" label="Observação" col="col-span-12" value={contact.note} onChange={handleInput} onBlur={handleBlur} />

                </div>

                <div className="flex justify-end gap-2 px-3 py-3 border-t border-gray-300 bg-[#F2F2F2] rounded-b-lg">
                    <div className="flex justify-end gap-3">
                        {
                            !newRow &&
                            <RemoveButtonCellRenderer node={modalNode} api={modalApi} modal={true} setOpen={setOpen} />
                        }
                        {
                            newRow ?
                                <Button target="success" onClick={() => {
                                    addContact();
                                    setOpen(false);
                                }}>
                                    Salvar
                                </Button>
                                :
                                <Button target="success" onClick={() => onClose(setOpen, setNewRow, gridRef, editingRowIndex, contact, autoSubmitOnModalSave)}>
                                    Salvar
                                </Button>
                        }
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default ContactsTable;
